// useGallery(APP#42)—— 相簿頁的狀態編排:載入中繼清單、刪除、清空。
//
// 只碰中繼(GalleryMeta,不含 blob);縮圖 / 檢視器各自用 useObjectUrl 依 id 取單筆
// blob 並管理 object URL 生命週期,避免一次把所有位元組載進記憶體。

import { useCallback, useEffect, useState } from 'react';
import { clearAll, deleteItem, galleryAvailable, getItem, listMeta } from './galleryDb';
import { filterByDevice, type GalleryMeta } from './galleryLib';

export interface UseGallery {
  items: GalleryMeta[];
  loading: boolean;
  /** 此瀏覽器是否支援本地相簿(IndexedDB)。 */
  available: boolean;
  refresh: () => Promise<void>;
  remove: (id: string) => Promise<void>;
  removeAll: () => Promise<void>;
}

/**
 * @param deviceId 選填:只顯示該裝置的媒體(裝置詳情頁進來);空 → 帳號總覽全部。
 */
export function useGallery(deviceId?: string): UseGallery {
  const [items, setItems] = useState<GalleryMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const available = galleryAvailable();

  const refresh = useCallback(async () => {
    setLoading(true);
    const all = await listMeta();
    setItems(filterByDevice(all, deviceId));
    setLoading(false);
  }, [deviceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    await deleteItem(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const removeAll = useCallback(async () => {
    await clearAll();
    setItems([]);
  }, []);

  return { items, loading, available, refresh, remove, removeAll };
}

/**
 * 依 id 取單筆 blob 並建立 object URL(縮圖 / 檢視器 / 下載共用)。
 * 卸載或 id 變更時撤銷舊 URL,避免洩漏。找不到 → url=null。
 */
export function useObjectUrl(id: string | null): { url: string | null; mime: string | null; filename: string | null } {
  const [state, setState] = useState<{ url: string | null; mime: string | null; filename: string | null }>({
    url: null,
    mime: null,
    filename: null,
  });

  useEffect(() => {
    let revoked = false;
    let createdUrl: string | null = null;
    if (!id) {
      setState({ url: null, mime: null, filename: null });
      return;
    }
    void (async () => {
      const item = await getItem(id);
      if (revoked) return;
      if (!item) {
        setState({ url: null, mime: null, filename: null });
        return;
      }
      createdUrl = URL.createObjectURL(item.blob);
      setState({ url: createdUrl, mime: item.mime, filename: item.filename });
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [id]);

  return state;
}
